import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FieldRenderer } from "@/components/forms/FieldRenderer";
import type { FormFieldRef } from "@/components/forms/types";
import { checkQuizAnswer, submitForm } from "@/api/forms";
import { ApiError } from "@/api/client";
import { isDisplayFieldType } from "@/lib/formBuilder/apiFieldTypes";
import { isListFieldType } from "@/lib/formBuilder/listChoicesMode";
import { buildSubmitPayload } from "@/lib/forms/buildSubmitPayload";
import { formatSubmitError } from "@/lib/http/formatSubmitError";
import { useFormVisibility } from "@/hooks/useFormVisibility";
import {
  collectFieldsInFormOrder,
  findFieldSection,
  isFieldVisible,
  pruneHiddenFieldValues,
} from "@/lib/forms/visibility";
import { fieldAdminLabel } from "@/lib/forms/fieldLabel";
import { parseQuizResult, quizCheckValue } from "@/lib/forms/quiz";
import type { QuizAnswerDetail, QuizConfig, QuizResult } from "@/types/quiz";
import type { ValuesByName } from "@/types/visibility";
import type { FieldDto } from "@/types/field";
import type { SectionDto } from "@/types/section";
import type { FormFieldAnswer } from "@/types/submit";

export function QuizForm({
  sections,
  rootFields = [],
  description = "",
  submitDisabled = false,
  showSectionLabels = true,
  quiz,
  formId,
  previewMode = false,
}: {
  sections: SectionDto[];
  rootFields?: FieldDto[];
  description?: string;
  submitDisabled?: boolean;
  showSectionLabels?: boolean;
  quiz: QuizConfig;
  formId?: string;
  previewMode?: boolean;
}) {
  const navigate = useNavigate();
  const publicFormId = formId?.trim() ?? "";
  const orderedFields = useMemo(
    () => collectFieldsInFormOrder(sections, rootFields),
    [rootFields, sections],
  );
  const { valuesByName, visibleByName, setFieldValue } = useFormVisibility(
    orderedFields,
  );
  const [history, setHistory] = useState<string[]>([]);
  const [isSending, setSending] = useState(false);
  const [isChecking, setChecking] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [checkResult, setCheckResult] = useState<QuizAnswerDetail | null>(null);
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);
  const fieldRef = useRef<FormFieldRef>(null);
  const labeledByName = useRef(
    new Map<string, { label: string; value: string }>(),
  );
  const submittedRef = useRef(false);

  const currentField =
    orderedFields.find(
      (field) =>
        visibleByName[field.name] !== false && !history.includes(field.name),
    ) ?? null;
  const visibleQuestionCount = orderedFields.filter(
    (field) =>
      visibleByName[field.name] !== false &&
      !isDisplayFieldType(field.type),
  ).length;
  const answeredCount = history.filter((name) => {
    const field = orderedFields.find((item) => item.name === name);
    return field && !isDisplayFieldType(field.type);
  }).length;
  const isLastQuestion = Boolean(
    currentField &&
      !orderedFields.some(
        (field) =>
          visibleByName[field.name] !== false &&
          !history.includes(field.name) &&
          field.name !== currentField.name,
      ),
  );

  useEffect(() => {
    setCheckResult(null);
    setFieldError(null);
  }, [currentField?.name]);

  const applyLabeledPayload = (payload: FormFieldAnswer[]): FormFieldAnswer[] =>
    payload.map((item) => {
      const field = orderedFields.find((entry) => entry.id === item.field_id);
      const labeled = field ? labeledByName.current.get(field.name) : undefined;
      if (labeled && String(item.value) === labeled.value) {
        return { ...item, value: labeled };
      }
      return item;
    });

  const commitCurrentValue = () => {
    if (!currentField) return null;
    const handle = fieldRef.current;
    if (handle && !handle.validate()) {
      setFieldError("Заполните поле");
      return null;
    }
    setFieldError(null);
    const raw = handle?.value ?? valuesByName[currentField.name] ?? "";
    if (handle?.labeledChoice) {
      labeledByName.current.set(currentField.name, handle.labeledChoice);
    } else {
      labeledByName.current.delete(currentField.name);
    }
    if (handle && !isDisplayFieldType(currentField.type)) {
      setFieldValue(currentField.name, handle.value);
    }
    return pruneHiddenFieldValues(orderedFields, {
      ...valuesByName,
      [currentField.name]: raw,
    });
  };

  const sendAnswers = async (values: ValuesByName) => {
    if (previewMode || submitDisabled || submittedRef.current) return;
    submittedRef.current = true;
    const payload = applyLabeledPayload(
      buildSubmitPayload(orderedFields, values),
    );
    setSending(true);
    setSubmitError(null);
    try {
      const response = await submitForm(publicFormId, { data: payload });
      const result = parseQuizResult(
        response.quizResult ?? response.quiz_result,
      );
      if (result) {
        setQuizResult(result);
        return;
      }
      navigate(`/success?form-id=${formId}`);
    } catch (err) {
      submittedRef.current = false;
      if (import.meta.env.DEV && err instanceof ApiError) {
        console.error("[chmforms] quiz submit failed", err.status, err.message);
      }
      setSubmitError(formatSubmitError(err));
    } finally {
      setSending(false);
    }
  };

  const goNext = async (values: ValuesByName) => {
    if (!currentField) return;
    const nextHistory = [...history, currentField.name];
    setHistory(nextHistory);
    setCheckResult(null);
    const remaining = orderedFields.filter(
      (field) =>
        !nextHistory.includes(field.name) && isFieldVisible(field, values),
    );
    if (remaining.length === 0) {
      if (previewMode) {
        setQuizResult({
          score: 0,
          maxScore: 0,
          percent: 0,
          passed: null,
          scoring: quiz.scoring,
          details: [],
        });
        return;
      }
      await sendAnswers(values);
    }
  };

  const confirmCurrent = async () => {
    if (!currentField || isSending || isChecking) return;
    if (submitDisabled && !previewMode) return;
    const nextValues = commitCurrentValue();
    if (!nextValues) return;

    const needsCheck =
      quiz.scoring === "per_question" &&
      isListFieldType(currentField.type) &&
      !checkResult &&
      !previewMode &&
      currentField.id != null;

    if (needsCheck) {
      setChecking(true);
      setSubmitError(null);
      try {
        const handle = fieldRef.current;
        const value = handle?.labeledChoice ?? quizCheckValue(handle?.value ?? "");
        const result = await checkQuizAnswer(publicFormId, {
          fieldId: Number(currentField.id),
          value,
        });
        setCheckResult(result);
      } catch (err) {
        setSubmitError(formatSubmitError(err));
      } finally {
        setChecking(false);
      }
      return;
    }

    await goNext(nextValues);
  };

  if (quizResult && !previewMode) {
    return <QuizResultView result={quizResult} fields={orderedFields} />;
  }

  if (quizResult && previewMode) {
    return (
      <p className="font-onest text-sm text-slate-600">
        На этом вопросы закончились.
      </p>
    );
  }

  const section = currentField
    ? findFieldSection(sections, currentField.name)
    : null;
  const sectionLabel = (section?.label ?? "").trim();

  return (
    <div className="space-y-4">
      {description.trim() ? (
        <p className="font-onest text-sm leading-relaxed text-ink-muted">
          {description.trim()}
        </p>
      ) : null}

      {visibleQuestionCount > 0 ? (
        <p className="font-onest text-xs font-medium uppercase tracking-wide text-slate-400">
          Вопрос {Math.min(answeredCount + (currentField ? 1 : 0), visibleQuestionCount)}{" "}
          из {visibleQuestionCount}
        </p>
      ) : null}

      {currentField ? (
        <div className="space-y-3">
          {showSectionLabels && sectionLabel ? (
            <p className="font-onest text-[11px] font-medium uppercase tracking-wide text-slate-400">
              {sectionLabel}
            </p>
          ) : null}
          <FieldRenderer
            key={currentField.name}
            field={currentField}
            visible
            layout="stacked"
            valuesByName={valuesByName}
            onFieldValueChange={setFieldValue}
            inputRef={(instance) => {
              fieldRef.current = instance;
            }}
          />
        </div>
      ) : null}

      {checkResult ? (
        <QuizCheckBanner result={checkResult} showAnswers={quiz.showCorrectAnswers} />
      ) : null}

      {fieldError ? (
        <p className="font-onest text-sm text-red-600" role="alert">
          {fieldError}
        </p>
      ) : null}
      {submitError ? (
        <div
          className="form-alert-error rounded-xl border border-red-200 bg-red-50 px-4 py-3"
          role="alert"
        >
          <p className="font-onest text-sm text-red-700">{submitError}</p>
        </div>
      ) : null}

      {currentField ? (
        submitDisabled && !previewMode ? (
          <p className="font-onest text-sm text-ink-muted">Приём ответов закрыт</p>
        ) : (
          <button
            type="button"
            disabled={isSending || isChecking}
            onClick={() => void confirmCurrent()}
            className="form-submit-btn inline-flex items-center justify-center rounded-xl bg-brand-500 px-6 py-2.5 font-onest text-[15px] font-medium text-white shadow-md shadow-brand-500/25 hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isChecking
              ? "Проверяем…"
              : checkResult || quiz.scoring === "end" || !isListFieldType(currentField.type)
                ? isLastQuestion
                  ? "Завершить"
                  : "Далее"
                : "Проверить"}
          </button>
        )
      ) : null}
    </div>
  );
}

function QuizCheckBanner({
  result,
  showAnswers,
}: {
  result: QuizAnswerDetail;
  showAnswers: boolean;
}) {
  return (
    <div
      className={[
        "rounded-xl border px-4 py-3",
        result.correct
          ? "border-emerald-200 bg-emerald-50"
          : "border-amber-200 bg-amber-50",
      ].join(" ")}
      role="status"
    >
      <p
        className={[
          "font-onest text-sm font-medium",
          result.correct ? "text-emerald-900" : "text-amber-900",
        ].join(" ")}
      >
        {result.correct ? "Верно" : "Неверно"}
        {result.maxPoints
          ? ` · ${result.points} из ${result.maxPoints}`
          : ""}
      </p>
      {showAnswers && result.correctAnswers?.length ? (
        <p className="mt-1 font-onest text-sm text-slate-700">
          Правильный ответ: {result.correctAnswers.join(", ")}
        </p>
      ) : null}
    </div>
  );
}

function QuizResultView({
  result,
  fields,
}: {
  result: QuizResult;
  fields: FieldDto[];
}) {
  const labelByName = new Map(
    fields.map((field) => [field.name, fieldAdminLabel(field)]),
  );
  const labelById = new Map(
    fields
      .filter((field) => field.id != null)
      .map((field) => [Number(field.id), fieldAdminLabel(field)]),
  );
  const answered = result.details.length;
  const correctCount = result.details.filter((detail) => detail.correct).length;
  const wrongCount = answered - correctCount;

  return (
    <div className="space-y-5">
      <div
        className={[
          "rounded-xl border px-4 py-4 sm:px-5",
          result.passed === true
            ? "border-emerald-200 bg-emerald-50"
            : result.passed === false
              ? "border-amber-200 bg-amber-50"
              : "border-slate-200 bg-slate-50",
        ].join(" ")}
      >
        <p className="font-unbounded text-3xl font-extrabold text-ink">
          {Math.round(result.percent)}%
        </p>
        <p className="mt-1 font-onest text-sm text-slate-700">
          {result.score} из {result.maxScore} баллов
        </p>
        {answered > 0 ? (
          <p className="mt-1 font-onest text-sm text-slate-700">
            {correctCount} верных : {wrongCount} неверных
          </p>
        ) : null}
        {result.passed == null ? null : (
          <p className="mt-2 font-onest text-sm font-medium text-slate-800">
            {result.passed ? "Зачёт" : "Не зачёт"}
          </p>
        )}
      </div>

      {result.details.length > 0 ? (
        <ul className="space-y-2">
          {result.details.map((detail) => {
            const label =
              labelById.get(detail.fieldId) ||
              labelByName.get(detail.name) ||
              detail.name;
            return (
              <li
                key={`${detail.fieldId}-${detail.name}`}
                className="rounded-xl border border-slate-200 px-3.5 py-3"
              >
                <p className="font-onest text-sm font-medium text-slate-800">
                  {label}
                </p>
                <p className="mt-0.5 font-onest text-xs text-slate-500">
                  {detail.correct ? "Верно" : "Неверно"} · {detail.points} /{" "}
                  {detail.maxPoints}
                </p>
                {detail.correctAnswers?.length ? (
                  <p className="mt-1 font-onest text-xs text-slate-600">
                    Правильный ответ: {detail.correctAnswers.join(", ")}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
